import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFetch, useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import { SalesRecording, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import SalesRecordingView from './sales-recording-view';
import useDocumentDownload from '../../../hooks/use-document-download';

const SalesRecordingContainer = () => {
  const apiClient = useApiClient();
  const { salesRecordingId } = useParams();
  const [submissionError, setSubmissionError] = useState<ApiError>();

  const { data: countryCodes, isLoading: isLoadingCC } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const {
    data: salesRecording, isLoading: isLoadingSR, error: errorSR, mutate,
  } = useFetch<SalesRecording>(apiClient.salesRecordingsClient.getSalesRecording, [salesRecordingId], true);
  const { data: tripMatch, isLoading: isLoadingTM } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [salesRecording?.tripMatchId], !!salesRecording?.tripMatchId);

  // Get the full list of sales recordings for the trip match so we can view the original sales recording data to compare against the adjustment
  const { data: salesRecordings } = useFetch<SalesRecording[]>(apiClient.tripMatchesClient.getTripMatchSalesRecordings, [tripMatch?.id], !!tripMatch?.id);
  const originalSalesRecording = salesRecordings?.find((sr) => sr.salesRecordingType === SalesRecordingType.General);

  const [onClickDocument, docError] = useDocumentDownload();

  const onClickDownload = () => {
    if (salesRecording?.document) {
      onClickDocument(salesRecording.document);
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (salesRecording && salesRecordingId) {
      const updatedSalesRecording = {
        ...salesRecording,
        internalNote: data.internalNote,
        invoiceDate: data.invoiceDate,
      };
      try {
        await apiClient.salesRecordingsClient.putSalesRecording(salesRecordingId, updatedSalesRecording);
        mutate();
        enqueueSnackbar('Updated successfully.', { variant: 'success' });
      } catch (e: any) {
        setSubmissionError(e);
        enqueueSnackbar('There was a problem updating.', { variant: 'default' });
      }
    }
  };

  return (
    <SalesRecordingView
      countryCodes={countryCodes}
      docError={docError}
      error={errorSR}
      isLoading={isLoadingCC || isLoadingSR || isLoadingTM}
      onClickDownload={onClickDownload}
      onSubmit={onSubmit}
      originalSalesRecording={salesRecordings && salesRecordings?.length > 1 ? originalSalesRecording : undefined}
      salesRecording={salesRecording}
      submissionError={submissionError}
      tripMatch={tripMatch}
    />
  );
};

export default SalesRecordingContainer;
